import React, { useState, Fragment, useCallback, useMemo, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  likeOrUnlikePost,
  editPost,
  deletePost,
  deleteComment,
  editComment,
  resharePost,
  fetchLikes,
  reportPost 
} from "../redux/actions/post";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { format } from "date-fns";
import { LazyLoadImage } from "react-lazy-load-image-component";
import {
  Box,
  Divider,
  IconButton,
  Typography,
  Modal,
  Backdrop,
  Fade,
  TextField,
  Button,
  MenuItem,
} from "@mui/material";
import { Popover, Transition } from "@headlessui/react";
import { EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import {
  ChatBubbleOutlineOutlined,
  ChatBubble,
} from "@mui/icons-material";
import FlexBetween from "../components/FlexBetween";
import Friend from "../components/Friend";
import WidgetWrapper from "../components/WidgetWrapper";
import VideoPlayer from "../components/Content/VideoPlayer";
import Comments from "../components/Comments/Comments";
import LinkPreviewCard from "../components/Social/LinkPreviewCard";
import { getAiMemberIdFromProfile } from "../utils/shopProfile";

// -- Import the Authentication Prompt Component --
import AuthenticationPrompt from "../components/Profile/AuthenticationPrompt";

const PostWidgetRedux = ({
  postId,
  postUserId,
  owner,
  name,
  description,
  profileType,
  location,
  images = [],
  videos = [],
  avatar,
  likes = [],
  comments = [],
  totalComments,
  createdAt,
  updatedAt,
  onShareSuccess,
  resharedBy,
  reshareDescription,
  reshareAvatar,
  link,
  isLikedByCurrentUser,
  avatarSize = "55px",
  isHighlighted = false,
  isProfilePost = false,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // -- Grab user & seller from Redux to check logged-in status --
  const { user } = useSelector((state) => state.user);
  const { seller } = useSelector((state) => state.seller);
  const isLoggedIn = Boolean(user?._id || seller?._id);

  // -- Local States --
  const loggedInUserId = user?._id;
  const loggedInSellerId = seller?._id;

  const [isComments, setIsComments] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDescription, setReportDescription] = useState("");

  // For modal
  const [isModalOpen, setIsModalOpen] = useState(false);

  // For sharing
  const [showReshareInput, setShowReshareInput] = useState(false);
  const [reshareInput, setReshareInput] = useState("");
  const [isReshare, setIsReshare] = useState(!!resharedBy);

  // For editing
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedDescription, setEditedDescription] = useState(description);
  const [editedReshareDescription, setEditedReshareDescription] =
    useState(reshareDescription);

  const formattedDate = format(new Date(createdAt), "PPP");
  const wasEdited = Boolean(
    updatedAt &&
    createdAt &&
    new Date(updatedAt).getTime() - new Date(createdAt).getTime() > 1000
  );
  const formattedEditedDate = wasEdited ? format(new Date(updatedAt), "PPP") : "";

  // For comment
  const [commentText, setCommentText] = useState("");
  const [newComments, setNewComments] = useState([]);

  // For like
  const [likeCount, setLikeCount] = useState(Array.isArray(likes) ? likes.length : 0);

  // Convert array of likes to see if current user has liked
  const currentProfileType = sessionStorage.getItem("currentProfile");
  const currentProfileId = useMemo(() => {
    return getAiMemberIdFromProfile(currentProfileType)
      || (currentProfileType === "User" ? user?._id : seller?._id);
  }, [user, seller, currentProfileType]);

  const isInitiallyLiked = useMemo(() => {
    return (
      Array.isArray(likes) &&
      likes.some((like) => like.likedBy === currentProfileId)
    );
  }, [likes, currentProfileId]);

  const [isLiked, setIsLiked] = useState(isInitiallyLiked);
  const [totalCommentsCount, setTotalCommentsCount] = useState(totalComments);

  const ownerId = owner && typeof owner === "object" ? owner._id : owner;
  const ownerName = owner?.name || name;
  const ownerAvatar = owner?.avatar?.url || avatar;
  const postOwnerIds = [ownerId, postUserId].filter(Boolean).map(String);
  const currentActorIds = [loggedInUserId, loggedInSellerId, currentProfileId].filter(Boolean).map(String);
  const canManagePost = postOwnerIds.some((id) => currentActorIds.includes(id));

  // For images
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [images]);
  const nextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };
  const prevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  // For editing scroll position
  const [scrollPosition, setScrollPosition] = useState(0);

  // -- State for showing authentication prompt --
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);

  // Scroll logic for edit mode
  const toggleEditMode = useCallback(
    (e) => {
      if (e) e.preventDefault();
      if (!isEditMode) {
        setScrollPosition(window.scrollY);
      } else {
        window.scrollTo(0, scrollPosition);
      }
      setIsEditMode(!isEditMode);
      if (isEditMode) {
        setEditedDescription(description);
      }
    },
    [description, isEditMode, scrollPosition]
  );

  const handleCancelReshare = () => {
    setShowReshareInput(false);
    setReshareInput("");
  };

  const stopPostModalPropagation = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const interactiveRegionProps = {
    onClick: stopPostModalPropagation,
    onMouseDown: stopPostModalPropagation,
    onPointerDown: stopPostModalPropagation,
    onTouchStart: stopPostModalPropagation,
  };

  const handleOpenPostModal = useCallback((event) => {
    if (
      event.target instanceof Element &&
      event.target.closest(
        [
          "button",
          "a",
          "textarea",
          "input",
          "select",
          "option",
          "label",
          "form",
          '[role="button"]',
          '[role="dialog"]',
          '[contenteditable="true"]',
          ".post-widget-popover",
          ".post-widget-reshare-container",
          ".post-widget-comments-section",
          ".link-post-preview-link",
          ".post-widget-thumbnail-image",
        ].join(", ")
      )
    ) {
      return;
    }

    setIsModalOpen(true);
  }, []);

  // We can scroll to the reshare input after it’s shown
  const reshareInputRef = useRef(null);
  const handleShowReshareInput = (options = {}) => {
    requireLogin(() => {
      setReshareInput("");
      if (options.closeModalFirst) {
        closePostModal();
      }
      setShowReshareInput(true);

      // Slight delay for the UI to update before scrolling
      setTimeout(() => {
        if (reshareInputRef.current) {
          reshareInputRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);
    });
  };

  const handleCancelEdit = () => {
    setEditedDescription(description);
    setEditedReshareDescription(reshareDescription);
    setIsEditMode(false);
  };

  // -- HELPER: requireLogin for any action
  const requireLogin = (action) => {
    if (!isLoggedIn) {
      setShowAuthPrompt(true);
      return;
    }
    action();
  };

  const closePostModal = useCallback(() => {
    setIsModalOpen(false);
    setIsComments(false);
  }, []);

  const handleCommentAction = useCallback((openInModal = false) => {
    requireLogin(() => {
      if (openInModal) {
        setIsComments(true);
        setIsModalOpen(true);
        return;
      }

      setIsComments((currentValue) => !currentValue);
    });
  }, [isLoggedIn]);

  // ========== LIKE OR UNLIKE POST ==============
  const handleLikeOrUnlikePost = () => {
    requireLogin(() => {
      const wasLiked = isLiked;
      const updatedLikeCount = wasLiked ? likeCount - 1 : likeCount + 1;

      // Optimistic UI update
      setIsLiked(!wasLiked);
      setLikeCount(updatedLikeCount);

      dispatch(likeOrUnlikePost(postId, currentProfileType)).catch((error) => {
        // Revert on error
        setIsLiked(wasLiked);
        setLikeCount(likeCount);
        toast.error("Failed to update like status");
      });
    });
  };

  // ========== EDIT POST SUBMIT ==============
  const handleEditSubmit = (e) => {
    e.preventDefault();
    requireLogin(() => {
      dispatch(editPost(postId, editedDescription, isReshare ? editedReshareDescription : undefined)).catch(
        () => {
          toast.error("Failed to edit post");
        }
      );
      setIsEditMode(false);
    });
  };

  // ========== DELETE POST ==============
  const handleDeletePost = (e) => {
    e.preventDefault();
    requireLogin(() => {
      dispatch(deletePost(postId)).catch(() => {
        toast.error("Failed to delete post");
      });
    });
  };

  // ========== SHARE POST ==============
  const handleSharePost = () => {
    requireLogin(() => {
      const reshareData = {
        profileType: currentProfileType,
        reshareDescription: reshareInput,
      };
      dispatch(resharePost(postId, reshareData))
        .then(() => {
          setShowReshareInput(false);
          setReshareInput("");
          toast.success("Post reshared successfully");
        })
        .catch(() => {
          toast.error("Failed to reshare post");
        });
    });
  };

  // ========== REPORT POST ==============
  const handleReportPost = () => {
    requireLogin(() => {
      if (!reportReason || !reportDescription) {
        toast.error("Please provide both a reason and a description.");
        return;
      }
      const reportData = {
        postId,
        reason: reportReason,
        description: reportDescription,
      };
      dispatch(reportPost(reportData))
        .then(() => {
          setShowReportModal(false);
          setReportReason("");
          setReportDescription("");
          toast.success("Post reported successfully.");
        })
        .catch(() => {
          toast.error("Failed to report post");
        });
    });
  };

  // ========== RENDER EDIT FORM ==============
  const renderEditForm = () => (
    <form onSubmit={handleEditSubmit} style={{ width: "100%" }}>
      <textarea
        autoFocus
        className="edit-description-textarea"
        value={isReshare ? editedReshareDescription : editedDescription}
        onChange={(e) =>
          isReshare
            ? setEditedReshareDescription(e.target.value)
            : setEditedDescription(e.target.value)
        }
        rows={3}
        style={{
          width: "100%",
          background: "none",
          border: "none",
          fontSize: "inherit",
          color: "inherit",
          lineHeight: "inherit",
          resize: "none",
          outline: "none",
        }}
      />
      <div className="flex justify-end space-x-2 mt-3">
        <button
          type="submit"
          className="inline-flex items-center justify-center rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition duration-200 ease-in-out"
        >
          Save Changes
        </button>
        <button
          type="button"
          onClick={handleCancelEdit}
          className="inline-flex items-center justify-center rounded-md bg-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition duration-200 ease-in-out"
        >
          Cancel
        </button>
      </div>
    </form>
  );

  // ========== PARSE HASHTAGS ==============
  const parseHashtags = useCallback(
    (text) => {
      const words = text.split(" ");
      return words.map((word, index) => {
        if (word.startsWith("#")) {
          const hashtag = word.substring(1);
          return (
            <span
              key={index}
              style={{ color: "green", cursor: "pointer" }}
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/hashtag/${hashtag}`);
              }}
            >
              {word + " "}
            </span>
          );
        }
        return word + " ";
      });
    },
    [navigate]
  );

  // ========== RENDER LINK PREVIEW ==============
  const renderLinkPreview = (link) => {
    if (!link || !link.url) return null;
    return <LinkPreviewCard preview={link} className="link-post-preview-container" />;
  };

  const renderEngagementControls = ({ extraClassName = "", openCommentsInModal = false } = {}) => (
    <FlexBetween
      className={`post-widget-engagement-row${extraClassName ? ` ${extraClassName}` : ""}`}
      mt="0.25rem"
      {...interactiveRegionProps}
    >
      <FlexBetween className="post-widget-engagement-cluster" gap="0">
        <FlexBetween className="post-widget-engagement-item" gap="0">
          <IconButton
            className="post-widget-engagement-button"
            onClick={handleLikeOrUnlikePost}
          >
            {isLiked ? (
              <img
                src="https://imagedelivery.net/mEHe7vWpwEHOhne-YSqzMw/b0d6f7b5-d727-4f1d-cda5-a1cf207eae00/public"
                alt="Liked"
                className="post-widget-like-icon-liked"
              />
            ) : (
              <img
                src="https://imagedelivery.net/mEHe7vWpwEHOhne-YSqzMw/1d169027-443a-4f85-8c7d-707000933f00/public"
                alt="Not Liked"
                className="post-widget-like-icon"
              />
            )}
          </IconButton>
          <Typography className="post-widget-engagement-count">{likeCount}</Typography>
        </FlexBetween>

        <FlexBetween className="post-widget-engagement-item" gap="0">
          <IconButton
            className="post-widget-engagement-button"
            onClick={() => handleCommentAction(openCommentsInModal)}
          >
            {totalCommentsCount > 0 ? (
              <ChatBubble className="post-widget-icons" />
            ) : (
              <ChatBubbleOutlineOutlined className="post-widget-icons" />
            )}
          </IconButton>
          <Typography className="post-widget-engagement-count">{totalCommentsCount}</Typography>
        </FlexBetween>

        <FlexBetween className="post-widget-engagement-item post-widget-engagement-item-share" gap="0">
          <IconButton className="post-widget-engagement-button" onClick={() => handleShowReshareInput({ closeModalFirst: Boolean(extraClassName) })} title="Share Post">
            <img
              src="https://imagedelivery.net/mEHe7vWpwEHOhne-YSqzMw/91301ff3-393c-42b0-6a3f-f4790206be00/public"
              className="post-widget-like-icon"
              alt="Share"
            />
          </IconButton>
        </FlexBetween>
      </FlexBetween>
    </FlexBetween>
  );

  const renderCommentsSection = (extraClassName = "") => (
    isComments ? (
      <div
        className={`post-widget-comments-section${extraClassName ? ` ${extraClassName}` : ""}`}
        {...interactiveRegionProps}
      >
        <Comments
          postId={postId}
          totalComments={totalCommentsCount}
          setTotalComments={setTotalCommentsCount}
          comments={comments}
          loggedInUserId={loggedInUserId}
          loggedInSellerId={loggedInSellerId}
          commentProfileType={currentProfileType}
          currentProfileId={currentProfileId}
          onCancelCommenting={() => setIsComments(false)}
        />
      </div>
    ) : null
  );

  return (
    <>
      <ToastContainer />
      <div
        className={isProfilePost ? "profile-post-card-host" : undefined}
        onClick={handleOpenPostModal}
        style={{ cursor: 'pointer' }}
      >
        <WidgetWrapper
          m="2rem 0"
          className={`post-widget-wrapper${isHighlighted ? " profile-post-highlight" : ""}${isProfilePost ? " profile-post-widget-wrapper" : ""}`}
        >
        {/* Three Dots for Edit/Report Modal */}
  <Popover className="post-widget-popover" onClick={stopPostModalPropagation}>
    <Popover.Button className="post-widget-popover-button">
      <EllipsisHorizontalIcon
        className="post-widget-icon"
        aria-hidden="true"
      />
    </Popover.Button>
    <Transition
      as={Fragment}
      enter="post-widget-transition-enter"
      enterFrom="post-widget-transition-enter-from"
      enterTo="post-widget-transition-enter-to"
      leave="post-widget-transition-leave"
      leaveFrom="post-widget-transition-leave-from"
      leaveTo="post-widget-transition-leave-to"
    >
      <Popover.Panel className="post-widget-popover-panel">
        <div className="post-widget-popover-content">
          {/* Owner-specific options */}
          {canManagePost ? (
            <>
              <button
                type="button"
                onClick={(e) => toggleEditMode(e)}
                className="post-widget-popover-item"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={(e) => handleDeletePost(e)}
                className="post-widget-popover-item"
              >
                Delete
              </button>
            </>
          ) : (
            // Non-owner-specific options (e.g., report)
            <button
              type="button"
              onClick={() => setShowReportModal(true)}
              className="post-widget-popover-item"
            >
              Report
            </button>
          )}
        </div>
      </Popover.Panel>
    </Transition>
  </Popover>


        {/* Reshare Information */}
        {resharedBy && (
          <div className="post-widget-reshare">
            <div className="post-widget-reshare-header">
              <img
                src={reshareAvatar || "default-avatar-url"}
                alt={resharedBy}
                className="post-widget-reshare-avatar"
              />
              <span className="post-widget-reshare-by">
                <span className="post-widget-reshare-label">Reshared by</span>
                <span className="post-widget-reshare-name">{resharedBy}</span>
              </span>
            </div>
            {reshareDescription && (
              <div className="post-widget-reshare-description">
                {isEditMode && isReshare ? renderEditForm() : reshareDescription}
              </div>
            )}
          </div>
        )}

        {/* Reshare Input (requires login, opened with handleShowReshareInput) */}
        {showReshareInput && (
          <div
            ref={reshareInputRef}
            className="post-widget-reshare-container"
            {...interactiveRegionProps}
          >
            <textarea
              className="post-widget-reshare-textarea"
              value={reshareInput}
              onChange={(e) => setReshareInput(e.target.value)}
              placeholder="Add a description to your reshare..."
            />
            <div className="post-widget-reshare-buttons-container">
              <button
                className="post-widget-reshare-button post-widget-cancel-button"
                onClick={handleCancelReshare}
              >
                Cancel
              </button>
              <button className="post-widget-reshare-button" onClick={handleSharePost}>
                Share
              </button>
            </div>
          </div>
        )}

        {/* Post Header: Friend Component */}
        <Friend
          className="post-widget-author-row"
          friendId={isReshare ? postUserId : ownerId}
          friendType={profileType}
          name={ownerName}
          subtitle={location}
          avatar={ownerAvatar}
          imageSize={avatarSize}
        >
          <div className="post-widget-date">
            <span>{formattedDate}</span>
            {wasEdited && <span className="post-widget-edited-date">Edited {formattedEditedDate}</span>}
          </div>
        </Friend>

        {/* Post Description or Edit Form */}
        <div>
          {isEditMode && !isReshare ? (
            renderEditForm()
          ) : (
            <Typography sx={{ mt: "1rem" }}>
              {parseHashtags(description)}
            </Typography>
          )}
        </div>

        {/* Link Preview if link object present */}
        {link && renderLinkPreview(link)}

        {/* Media Content: Images and Videos */}
        <div className="post-widget-media-container">
          {images.length > 0 && (
            <div className="post-widget-image-gallery">
              {images.length > 1 ? (
                <div className="post-widget-slideshow-container">
                  <div className="post-widget-slideshow">
                    <button onClick={prevImage} className="post-widget-prev-button">
                      &#10094;
                    </button>
                    <LazyLoadImage
                      src={images[currentImageIndex].url}
                      alt={`Slide ${currentImageIndex + 1}`}
                      className="post-widget-slideshow-image"
                      placeholderSrc="path/to/placeholder/image.jpg"
                    />
                    <button onClick={nextImage} className="post-widget-next-button">
                      &#10095;
                    </button>
                  </div>
                  <div className="post-widget-thumbnails-container">
                    {images.map((image, index) => (
                      <LazyLoadImage
                        key={index}
                        src={image.url}
                        alt={`Thumbnail ${index + 1}`}
                        className={`post-widget-thumbnail-image ${
                          index === currentImageIndex ? "active" : ""
                        }`}
                        onClick={() => setCurrentImageIndex(index)}
                        width={60}
                        height={60}
                        placeholderSrc="path/to/placeholder/image.jpg"
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <LazyLoadImage
                  key={0}
                  width="100%"
                  height="auto"
                  alt="post image 1"
                  className="post-widget-image"
                  src={images[0].url}
                  placeholderSrc="path/to/placeholder/image.jpg"
                />
              )}
            </div>
          )}

          {videos.length > 0 && (
            <div className="post-widget-video-gallery">
              {videos.map((video, index) => (
                <div key={index} className="post-widget-video-container">
                  <VideoPlayer videoUrl={video.key} />
                </div>
              ))}
            </div>
          )}
        </div>

        {renderEngagementControls({ openCommentsInModal: true })}
        {!isModalOpen ? renderCommentsSection() : null}
      </WidgetWrapper>
      </div>

      {/* Post Modal */}
      <Modal
        open={isModalOpen}
        onClose={closePostModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={isModalOpen}>
          <Box
            className={`post-modal-shell${isProfilePost ? " profile-post-modal-shell" : ""}`}
            onClick={stopPostModalPropagation}
          >
            <button
              type="button"
              className="post-modal-close-button"
              aria-label="Close post modal"
              onClick={closePostModal}
            >
              &times;
            </button>
            <div
              className="post-modal-click-target"
              onClick={() => {
                closePostModal();
                navigate(`/post/${postId}`);
              }}
              style={{ cursor: 'pointer' }}
            >
              <WidgetWrapper m="0" className="post-widget-wrapper post-modal-post-card">
              {/* Render the post content in modal */}
              <Friend
                className="post-widget-author-row"
                friendId={isReshare ? postUserId : ownerId}
                friendType={profileType}
                name={ownerName}
                subtitle={location}
                avatar={ownerAvatar}
                imageSize={avatarSize}
              >
                <div className="post-widget-date">
                  <span>{formattedDate}</span>
                  {wasEdited && <span className="post-widget-edited-date">Edited {formattedEditedDate}</span>}
                </div>
              </Friend>
              <Typography sx={{ mt: "1rem" }}>
                {parseHashtags(description)}
              </Typography>
              {link && renderLinkPreview(link)}
              <div className="post-widget-media-container">
                {images.length > 0 && (
                  <div className="post-widget-image-gallery">
                    {images.length > 1 ? (
                      <div className="post-widget-slideshow-container">
                        <div className="post-widget-slideshow">
                          <button onClick={prevImage} className="post-widget-prev-button">
                            &#10094;
                          </button>
                          <LazyLoadImage
                            src={images[currentImageIndex].url}
                            alt={`Slide ${currentImageIndex + 1}`}
                            className="post-widget-slideshow-image"
                            placeholderSrc="path/to/placeholder/image.jpg"
                          />
                          <button onClick={nextImage} className="post-widget-next-button">
                            &#10095;
                          </button>
                        </div>
                        <div className="post-widget-thumbnails-container">
                          {images.map((image, index) => (
                            <LazyLoadImage
                              key={index}
                              src={image.url}
                              alt={`Thumbnail ${index + 1}`}
                              className={`post-widget-thumbnail-image ${
                                index === currentImageIndex ? "active" : ""
                              }`}
                              onClick={() => setCurrentImageIndex(index)}
                              width={60}
                              height={60}
                              placeholderSrc="path/to/placeholder/image.jpg"
                            />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <LazyLoadImage
                        key={0}
                        width="100%"
                        height="auto"
                        alt="post image 1"
                        className="post-widget-image"
                        src={images[0].url}
                        placeholderSrc="path/to/placeholder/image.jpg"
                      />
                    )}
                  </div>
                )}
                {videos.length > 0 && (
                  <div className="post-widget-video-gallery">
                    {videos.map((video, index) => (
                      <div key={index} className="post-widget-video-container">
                        <VideoPlayer videoUrl={video.key} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="post-modal-interactive-region" {...interactiveRegionProps}>
                {renderEngagementControls({ extraClassName: "post-widget-modal-engagement-row" })}
                {renderCommentsSection("post-widget-modal-comments-section")}
              </div>
              </WidgetWrapper>
            </div>
          </Box>
        </Fade>
      </Modal>

      {/* Report Post Modal */}
      {showReportModal && (
        <div
          className="report-modal-backdrop"
          onClick={() => setShowReportModal(false)}
        >
          <div
            className="report-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="post-modal-close-button report-modal-close-button"
              aria-label="Close report modal"
              onClick={() => setShowReportModal(false)}
            >
              &times;
            </button>
            <h2 className="report-modal-title">Report Post</h2>
            <div className="report-modal-field">
              <label htmlFor="report-reason" className="report-modal-label">
                Reason
              </label>
              <select
                id="report-reason"
                className="report-modal-select"
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
              >
                <option value="">Select Reason</option>
                <option value="Spam">Spam</option>
                <option value="Harassment">Harassment</option>
                <option value="Inappropriate Content">
                  Inappropriate Content
                </option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="report-modal-field">
              <label htmlFor="report-description" className="report-modal-label">
                Description
              </label>
              <textarea
                id="report-description"
                className="report-modal-textarea"
                rows="4"
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                placeholder="Enter description..."
              />
            </div>
            <button
              className="report-modal-button"
              onClick={handleReportPost}
            >
              Submit Report
            </button>
          </div>
        </div>
      )}


      {/* Authentication Prompt if user/seller is not logged in */}
      {showAuthPrompt &&
        ReactDOM.createPortal(
          <div className="auth-modal-backdrop" onClick={() => setShowAuthPrompt(false)}>
            <div
              className="auth-modal-content"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <AuthenticationPrompt forceVisible />
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default PostWidgetRedux;





